import { apiClient } from '@/lib/api/client';
import type { Shop, ImageObject, ShopAddress } from '@/lib/types';
import { getImageUrl, sortImagesByOrder } from '@/lib/utils';
import type { ShopFilters, CreateShopData, UpdateShopData } from '../types/shops.types';

interface ApiData<T> {
  data: T;
}

// Server shop response type
interface ServerShopDayHours {
  startDateMorning?: string;
  closeDateMorning?: string;
  startingDateAfternoon?: string;
  closeDateAfternoon?: string;
  enable: boolean;
}

interface ServerShopMetrics {
  views: number;
  dailyViews: number;
  weeklyViews: number;
  monthlyViews: number;
  uniqueVisitors: number;
  countriesData?: Record<string, number>;
  deviceData?: Record<string, number>;
}

interface ServerShopReviews {
  averageRating: number;
  totalReviews: number;
}

// Shipping rate from server
interface ServerShippingRate {
  refId: string;
  name: string;
  description?: string;
  price: string;
  prices?: {
    comoros: number;
    usd: number;
    euro: string | number;
  };
  currency?: string;
  freeShippingThreshold?: string | null;
  estimatedDeliveryDays?: number | null;
  maxDeliveryDays?: number | null;
  maxWeight?: string | null;
  maxDimensions?: string | null;
  availableCountries?: string[];
  availableRegions?: string[] | null;
  isActive: boolean;
  additionalFees?: string | null;
  deliveryType?: string;
  deliveryCompany?: string | null;
}

// Shop promotion from server
interface ServerShopPromotion {
  refId: string;
  name: string;
  displayName?: string;
  type: string;
  value: string;
  startDate: string;
  endDate: string;
  status: string;
  currentUsage: number;
  maxUsage?: number | null;
  productsCount: number;
  badgeColor?: string;
  createdAt: string;
}

interface ServerShop {
  id: string;
  refId?: string;
  name: string;
  slug?: string;
  description?: string;
  status?: 'online' | 'offline' | 'suspended';
  delivery?: 'store' | 'home' | 'both';
  phoneNumber?: string;
  phone?: string;
  email?: string;
  manager?: string;
  address?: ShopAddress;
  // Detail endpoint returns images array
  images?: ImageObject[];
  // List endpoint returns single image object
  image?: ImageObject;
  // List endpoint returns city/country as strings
  city?: string;
  country?: string;
  disableCommentReason?: string;
  // List-specific field
  hasActivePromotions?: boolean;
  // Stats (backend uses productsCount and activeOrdersCount)
  productsCount?: number;
  activeOrdersCount?: number;
  productCount?: number;
  orderCount?: number;
  totalProducts?: number;
  totalOrders?: number;
  rating?: number;
  reviewCount?: number;
  subscriberCount?: number;
  // Metrics and reviews
  metrics?: ServerShopMetrics;
  reviews?: ServerShopReviews;
  // Day-specific hours (structured format from server)
  monday?: ServerShopDayHours;
  tuesday?: ServerShopDayHours;
  wednesday?: ServerShopDayHours;
  thursday?: ServerShopDayHours;
  friday?: ServerShopDayHours;
  saturday?: ServerShopDayHours;
  sunday?: ServerShopDayHours;
  // Legacy format
  availabilities?: Array<{
    day: string;
    startDateMorning?: string;
    closeDateMorning?: string;
    startingDateAfternoon?: string;
    closeDateAfternoon?: string;
    enable: boolean;
  }>;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
  // Shipping and promotions
  shippingRates?: ServerShippingRate[];
  promotions?: ServerShopPromotion[];
}

/**
 * Converts availabilities array to day-specific hours format
 */
function convertAvailabilitiesToDayHours(
  availabilities?: ServerShop['availabilities']
): Record<string, ServerShopDayHours> {
  const dayHours: Record<string, ServerShopDayHours> = {};

  if (!availabilities) return dayHours;

  const dayMap: Record<string, string> = {
    'MONDAY': 'monday',
    'TUESDAY': 'tuesday',
    'WEDNESDAY': 'wednesday',
    'THURSDAY': 'thursday',
    'FRIDAY': 'friday',
    'SATURDAY': 'saturday',
    'SUNDAY': 'sunday',
    'monday': 'monday',
    'tuesday': 'tuesday',
    'wednesday': 'wednesday',
    'thursday': 'thursday',
    'friday': 'friday',
    'saturday': 'saturday',
    'sunday': 'sunday',
  };

  for (const avail of availabilities) {
    const day = dayMap[avail.day] || avail.day.toLowerCase();
    dayHours[day] = {
      startDateMorning: avail.startDateMorning,
      closeDateMorning: avail.closeDateMorning,
      startingDateAfternoon: avail.startingDateAfternoon,
      closeDateAfternoon: avail.closeDateAfternoon,
      enable: avail.enable,
    };
  }

  return dayHours;
}

/**
 * Transforms server shop to frontend Shop
 * Handles both list format (single image, city/country as strings)
 * and detail format (images array, city/country in address)
 */
// Le formulaire web-pro utilise `both` ; le backend utilise `home_and_store`.
// On mappe à la frontière du service (form <-> serveur).
function toServerDelivery(d?: string): string | undefined {
  return d === 'both' ? 'home_and_store' : d;
}
function fromServerDelivery(d?: string): 'store' | 'home' | 'both' {
  return d === 'home_and_store' ? 'both' : ((d as 'store' | 'home') ?? 'both');
}

function transformShop(serverShop: ServerShop): Shop {
  // Handle images - list returns single `image`, detail returns `images` array
  let logoUrl: string | undefined;
  let bannerUrl: string | undefined;

  if (serverShop.images && serverShop.images.length > 0) {
    // Detail format - images array
    const sortedImages = sortImagesByOrder(serverShop.images);
    logoUrl = getImageUrl(sortedImages[0]);
    bannerUrl = getImageUrl(sortedImages[1] || sortedImages[0]);
  } else if (serverShop.image) {
    // List format - single image object
    logoUrl = getImageUrl(serverShop.image);
    bannerUrl = undefined;
  }

  // Handle city/country - list returns directly, detail returns in address
  const city = serverShop.city || serverShop.address?.city;
  const country = serverShop.country || serverShop.address?.country;

  // Convert availabilities to day-specific hours if not already present
  const dayHours = convertAvailabilitiesToDayHours(serverShop.availabilities);

  return {
    id: serverShop.id,
    refId: serverShop.refId,
    name: serverShop.name,
    slug: serverShop.slug,
    description: serverShop.description,
    status: serverShop.status,
    delivery: fromServerDelivery(serverShop.delivery),
    phoneNumber: serverShop.phoneNumber || serverShop.phone,
    phone: serverShop.phone || serverShop.phoneNumber,
    email: serverShop.email,
    manager: serverShop.manager,
    address: serverShop.address,
    images: serverShop.images,
    logo: logoUrl,
    banner: bannerUrl,
    city: city,
    country: country,
    isActive: serverShop.status === 'online',
    disableCommentReason: serverShop.disableCommentReason,
    // List-specific
    hasActivePromotions: serverShop.hasActivePromotions,
    // Stats (map backend productsCount/activeOrdersCount to frontend productCount/orderCount)
    productCount: serverShop.productsCount ?? serverShop.productCount ?? serverShop.totalProducts,
    orderCount: serverShop.activeOrdersCount ?? serverShop.orderCount ?? serverShop.totalOrders,
    totalProducts: serverShop.productsCount ?? serverShop.totalProducts,
    totalOrders: serverShop.activeOrdersCount ?? serverShop.totalOrders,
    rating: serverShop.rating,
    reviewCount: serverShop.reviewCount,
    subscriberCount: serverShop.subscriberCount,
    // Metrics and reviews
    metrics: serverShop.metrics,
    reviews: serverShop.reviews,
    // Day-specific hours (prefer server format, fallback to converted availabilities)
    monday: serverShop.monday || dayHours.monday,
    tuesday: serverShop.tuesday || dayHours.tuesday,
    wednesday: serverShop.wednesday || dayHours.wednesday,
    thursday: serverShop.thursday || dayHours.thursday,
    friday: serverShop.friday || dayHours.friday,
    saturday: serverShop.saturday || dayHours.saturday,
    sunday: serverShop.sunday || dayHours.sunday,
    availabilities: serverShop.availabilities,
    createdAt: serverShop.createdAt || serverShop.created_at || '',
    created_at: serverShop.created_at || serverShop.createdAt,
    updatedAt: serverShop.updatedAt || serverShop.updated_at || '',
    // Shipping and promotions
    shippingRates: serverShop.shippingRates,
    promotions: serverShop.promotions,
  };
}

export const shopsService = {
  async getShops(filters?: ShopFilters): Promise<Shop[]> {
    // Server returns { message, data: Shop[] }
    const response = await apiClient.get<ApiData<ServerShop[]>>('/shop/all/shops');
    let shops = (response.data?.data ?? []).map(transformShop);

    // Client-side filtering if needed
    if (filters?.search) {
      const searchLower = filters.search.toLowerCase();
      shops = shops.filter(
        (shop) =>
          shop.name.toLowerCase().includes(searchLower) ||
          shop.description?.toLowerCase().includes(searchLower)
      );
    }
    if (filters?.isActive !== undefined) {
      shops = shops.filter((shop) => shop.isActive === filters.isActive);
    }

    return shops;
  },

  async getShop(id: string): Promise<Shop | null> {
    const response = await apiClient.get<ApiData<ServerShop>>(`/shop/?id=${id}`);
    const data = response.data?.data;
    if (!data) return null;
    return transformShop(data);
  },

  async createShop(data: CreateShopData, images: File[]): Promise<Shop | null> {
    const formData = new FormData();
    formData.append('name', data.name ?? '');
    formData.append('manager', data.manager ?? '');
    formData.append('phoneNumber', data.phone ?? '');
    formData.append('description', data.description ?? '');
    formData.append('shippingRates', data.shippingRates?.join(',') ?? '');
    formData.append('address', data.address ?? '');

    // Build availabilities array from day-specific hours (matching mobile format)
    const availabilities = [
      { day: 'monday', id: 'monday', ...data.monday },
      { day: 'tuesday', id: 'tuesday', ...data.tuesday },
      { day: 'wednesday', id: 'wednesday', ...data.wednesday },
      { day: 'thursday', id: 'thursday', ...data.thursday },
      { day: 'friday', id: 'friday', ...data.friday },
      { day: 'saturday', id: 'saturday', ...data.saturday },
      { day: 'sunday', id: 'sunday', ...data.sunday },
    ];
    formData.append('availabilities', JSON.stringify(availabilities));
    if (data.delivery) formData.append('delivery', toServerDelivery(data.delivery) ?? 'home_and_store');

    // Add all images
    images.forEach((image) => {
      formData.append('images', image);
    });

    const response = await apiClient.post<ApiData<Shop>>('/shop/create', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data?.data ?? null;
  },

  async updateShop(id: string, data: UpdateShopData): Promise<Shop | null> {
    // Send data matching backend RequestUpdateShopInfoDto (delivery mappé both -> home_and_store).
    const payload = { ...data, ...(data.delivery ? { delivery: toServerDelivery(data.delivery) } : {}) };
    const response = await apiClient.put<ApiData<ServerShop>>(`/shop/update/info?id=${id}`, payload);
    const shopData = response.data?.data;
    if (!shopData) return null;
    return transformShop(shopData);
  },

  async updateShopStatus(id: string, status: 'online' | 'offline'): Promise<Shop | null> {
    const response = await apiClient.put<ApiData<Shop>>('/shop/update/status', {
      id,
      status,
    });
    return response.data?.data ?? null;
  },

  // Action rapide (page détail) : bascule la capacité de livraison/retrait (both -> home_and_store).
  async updateShopDelivery(id: string, delivery: 'store' | 'home' | 'both'): Promise<Shop | null> {
    const response = await apiClient.put<ApiData<ServerShop>>('/shop/update/delivery', {
      id,
      delivery: toServerDelivery(delivery),
    });
    const shopData = response.data?.data;
    return shopData ? transformShop(shopData) : null;
  },

  async deleteShop(id: string, reason?: string, targetShopRefId?: string): Promise<void> {
    await apiClient.delete('/shop/delete', {
      data: { shopRefId: id, reason, targetShopRefId },
    });
  },

  /**
   * Updates shop images using the 'orders' approach required by the server.
   * @param id - Shop ID
   * @param orders - Array of image order objects (existing images to keep + new file placeholders)
   * @param newImages - New image files to upload
   */
  async updateShopImages(
    id: string,
    orders: Array<{ url: string; order: number; isFile: boolean; name: string; id: string }>,
    newImages: File[]
  ): Promise<Shop | null> {
    // Debug: log what we're sending
    console.log('[updateShopImages] Sending:', {
      id,
      orders: JSON.stringify(orders, null, 2),
      files: newImages.map(f => ({ name: f.name, size: f.size, type: f.type })),
    });

    const formData = new FormData();
    formData.append('id', id);
    formData.append('orders', JSON.stringify(orders));
    newImages.forEach((image) => {
      formData.append('images', image);
    });

    const response = await apiClient.put<ApiData<Shop>>('/shop/update/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data?.data ?? null;
  },
};
